/**
 * DailyRefreshCard — Operator controls for the daily/weekly refresh
 * scheduler that keeps the 10-year procurement history current.
 *
 * Data source:
 *   GET  /api/scraper/daily-refresh           — config + last-run stats
 *   PUT  /api/scraper/daily-refresh/config    — update enable flag
 *   POST /api/scraper/daily-refresh/run-now   — manual trigger
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock, Clock, Loader2, Play, Power, AlertTriangle, CheckCircle,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './SchedulerHealthCard.css';

function timeAgo(iso) {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return 'just now';
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function DailyRefreshCard() {
  const qc = useQueryClient();
  const [runningKind, setRunningKind] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['scraper', 'daily-refresh'],
    queryFn: () => apiClient.get('/api/scraper/daily-refresh'),
    refetchInterval: 30000,
  });

  const toggle = useMutation({
    mutationFn: (enabled) => apiClient.put('/api/scraper/daily-refresh/config', { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scraper', 'daily-refresh'] }),
  });

  const runNow = useMutation({
    mutationFn: (kind) => apiClient.post('/api/scraper/daily-refresh/run-now', { kind }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scraper', 'daily-refresh'] }),
    onSettled: () => setRunningKind(null),
  });

  if (isLoading) {
    return (
      <div className="schcard schcard--loading">
        <Loader2 size={16} className="schcard-spin" />
        <span>Loading daily refresh config…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="schcard schcard--error">
        <AlertTriangle size={16} />
        <span>Daily refresh unreachable: {error.message}</span>
      </div>
    );
  }

  const enabled = !!data?.enabled;
  const running = !!data?.running;
  const state = enabled ? 'running' : 'disabled';
  const stateLabel = enabled ? 'ENABLED' : 'DISABLED';
  const StateIcon = enabled ? CheckCircle : Power;

  const handleRun = (kind) => {
    setRunningKind(kind);
    runNow.mutate(kind);
  };

  return (
    <div className={`schcard schcard--${state}`}>
      <div className="schcard-header">
        <div className="schcard-header-left">
          <CalendarClock size={16} className="schcard-icon" />
          <span className="schcard-title">Daily Refresh</span>
        </div>
        <div className={`schcard-badge schcard-badge--${state}`}>
          <StateIcon size={12} />
          <span>{stateLabel}</span>
        </div>
      </div>

      <div className="schcard-grid">
        <div className="schcard-metric">
          <span className="schcard-metric-label">Daily schedule</span>
          <span className="schcard-metric-value">
            {data?.daily_cron} · last {data?.daily_lookback_days}d
          </span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Weekly schedule</span>
          <span className="schcard-metric-value">
            {data?.weekly_cron} · last {data?.weekly_lookback_days}d
          </span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Last daily run</span>
          <span className="schcard-metric-value">
            {timeAgo(data?.last_daily_run)}{' '}
            {data?.last_daily_queued > 0 && (
              <span className="is-muted">· {data.last_daily_queued} queued</span>
            )}
          </span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Last weekly run</span>
          <span className="schcard-metric-value">
            {timeAgo(data?.last_weekly_run)}{' '}
            {data?.last_weekly_queued > 0 && (
              <span className="is-muted">· {data.last_weekly_queued} queued</span>
            )}
          </span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Acq types</span>
          <span className="schcard-metric-value">
            {(data?.acquisition_types || []).join(' + ')}
          </span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Cron runtime</span>
          <span className={`schcard-metric-value ${data?.cron_running ? 'is-good' : 'is-bad'}`}>
            {data?.cron_running ? 'registered' : 'not started'}
          </span>
        </div>
      </div>

      {data?.last_error && (
        <div className="schcard-nudges">
          <div className="schcard-nudge">
            <AlertTriangle size={12} />
            <span>Last error: {data.last_error}</span>
          </div>
        </div>
      )}
      {data?.last_skip_reason && (
        <div className="schcard-nudges">
          <div className="schcard-nudge">
            <Clock size={12} />
            <span>Last skipped: {data.last_skip_reason}</span>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          className="sd-btn sd-btn-ghost"
          disabled={toggle.isPending}
          onClick={() => toggle.mutate(!enabled)}
        >
          <Power size={12} />
          {toggle.isPending ? 'saving…' : enabled ? 'Disable' : 'Enable'}
        </button>
        <button
          className="sd-btn sd-btn-ghost"
          disabled={!enabled || running || runNow.isPending}
          onClick={() => handleRun('daily')}
          title={!enabled ? 'Enable first' : running ? 'A run is already in progress' : 'Fire the daily refresh now'}
        >
          {runningKind === 'daily' ? <Loader2 size={12} className="schcard-spin" /> : <Play size={12} />}
          Run daily now
        </button>
        <button
          className="sd-btn sd-btn-ghost"
          disabled={!enabled || running || runNow.isPending}
          onClick={() => handleRun('weekly')}
          title={!enabled ? 'Enable first' : running ? 'A run is already in progress' : 'Fire the weekly refresh now'}
        >
          {runningKind === 'weekly' ? <Loader2 size={12} className="schcard-spin" /> : <Play size={12} />}
          Run weekly now
        </button>
      </div>

      <div className="schcard-footer">
        Runs at 04:00 PT daily + Sunday 03:00 PT weekly · lookback
        {' '}{data?.daily_lookback_days}d / {data?.weekly_lookback_days}d · 2 jobs per run
        (IT Goods + IT Services, all depts, bulk export)
      </div>
    </div>
  );
}
